import type React from "react";
import { useEffect, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  Calendar,
  ChevronDown,
  Clock,
  Eye,
  FileText,
  Mail,
  MessageCircle,
  Share,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import useImagingSharing from "@/features/imagings/hooks/useImagingNotification";
import { useReportSharing } from "@/features/imagings/hooks/useReportSharing";
import { useSharedRecords } from "@/features/imagings/hooks/useSharedRecords";
import {
  imagingStudyNotificationSchema,
  type Imaging,
  type ImagingStudyNotification,
} from "@/features/imagings/schemas/imagings";
import type { Report } from "@/features/imagings/types/reports";
import type { User } from "@/features/users/schemas/user";
import { useModalityName } from "@/shared/components/ModalityBadge/useModalityBadge";
import { formatDate } from "@/shared/utils/date";
import { formatPhoneNumber, unformatPhoneNumber } from "@/utils/phoneMasks";

/**
 * Props para el componente ShareDialog
 */
interface ShareDialogProps {
  isOpen: boolean;
  onClose: () => void;
  imaging: Imaging;
  reports?: Report[];
  institution?: User;
}

/**
 * Componente para compartir un estudio de imagen con secciones separadas para Email y WhatsApp
 */
export function ShareDialog({
  isOpen,
  onClose,
  imaging,
  institution,
  reports = [],
}: ShareDialogProps): React.JSX.Element {
  const { shareImaging } = useImagingSharing();
  const { shareReport, isSharing } = useReportSharing();
  const { modalityName, isLoading, isSuccess } = useModalityName(imaging.modality);

  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  // Traer historial real de compartidos
  const { data: trackingData, isLoading: isLoadingTracking } = useSharedRecords({
    imaging,
  });

  // Helpers visuales para el historial
  const getStatusColor = (status: string) => {
    switch (status) {
      case "viewed":
        return "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400";
      case "active":
        return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400";
      case "expired":
      case "failed":
      case "bounce":
        return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400";
      default:
        return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "viewed":
        return "Visto";
      case "active":
        return "Activo";
      case "expired":
        return "Expirado";
      case "failed":
        return "Falló";
      case "bounce":
        return "Rebota";
      case "open":
        return "Abierto";
      case "sended_api":
        return "Enviado";
      default:
        return status;
    }
  };

  // Form setup
  const form = useForm<ImagingStudyNotification>({
    resolver: zodResolver(imagingStudyNotificationSchema),
    defaultValues: {
      type: "email",
      days: 180,
      email: "",
      phone: "",
      study: imaging.iuid,
      uid: imaging.uid,
      institution_id: imaging.institution_id,
      patient_name: imaging.patient_name.replace(/[^a-zA-Z]/g, " ").trim(),
      institution_name: imaging.institution_name || "No hay nombre de la institución",
      study_description: imaging.description || "No hay descripción del estudio",
      logo: institution?.logo_email || "",
      email_center: institution?.email_support || "",
      phone_center: institution?.phone_support || "",
      phoneCenter: institution?.phone_support || "",
      institution_email: institution?.email || "",
      modality: `${modalityName} (${imaging.modality})`,
    },
  });

  const handleShare = async (method: "email" | "ws") => {
    const values = form.getValues();

    // Validaciones básicas antes de enviar
    if (method === "email" && !values.email) {
      toast.error("Por favor ingresa un correo electrónico válido");
      return;
    }
    if (method === "ws" && !values.phone) {
      toast.error("Por favor ingresa un número de WhatsApp válido");
      return;
    }

    try {
      const data = { ...values, type: method } as ImagingStudyNotification;

      await shareImaging.mutateAsync(data);

      if (reports.length > 0) {
        for (const report of reports) {
          await shareReport.mutateAsync({
            ...data,
            report,
            method: method === "ws" ? "whatsapp" : "email",
            recipient: method === "ws" ? data.phone! : data.email || "",
          } as any);
        }
      }

      toast.success(
        `Estudio compartido exitosamente por ${method === "ws" ? "WhatsApp" : "Email"}`
      );
      onClose();
      form.reset();
    } catch (error: any) {
      console.error("Error compartiendo estudio:", error);
      toast.error(error?.message || "Error al compartir el estudio");
    }
  };

  useEffect(() => {
    if (isSuccess) {
      form.reset({
        ...form.getValues(),
        modality: `${modalityName} (${imaging.modality})`,
      });
    }
  }, [isSuccess, modalityName, imaging.modality, form]);

  if (isLoading) {
    return <div className="p-10 text-center">Cargando...</div>;
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          form.reset();
          onClose();
        }
      }}
    >
      <DialogContent className="top-auto! bottom-0! left-0! translate-x-0! translate-y-0! w-full! max-w-none! rounded-b-none! rounded-t-3xl! sm:top-[50%]! sm:bottom-auto! sm:left-[50%]! sm:-translate-x-1/2! sm:-translate-y-1/2! sm:rounded-2xl! sm:w-[95vw]! sm:max-w-md! md:max-w-lg! max-h-[90dvh] sm:max-h-[85vh] flex flex-col p-0 overflow-hidden bg-slate-50 dark:bg-slate-950 sm:shadow-2xl shadow-[0_-8px_30px_rgb(0,0,0,0.12)]">
        <DialogHeader className="p-4 sm:p-6 pb-4 bg-white dark:bg-slate-900 border-b shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
              <Share className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-xl font-semibold truncate">
                Compartir Estudio
              </DialogTitle>
              <p className="text-sm text-muted-foreground mt-1 truncate">
                Selecciona el método de envío para el paciente
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 p-4 sm:p-6 space-y-4 overflow-y-auto custom-scrollbar touch-pan-y max-h-[65dvh] sm:max-h-[60dvh]">
          {/* Email Section */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border p-5 shadow-sm space-y-4">
            <div className="flex items-center gap-3 text-blue-600 dark:text-blue-400">
              <div className="h-8 w-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                <Mail className="h-4 w-4" />
              </div>
              <h3 className="font-semibold text-sm">Compartir por Correo</h3>
            </div>

            <Form {...form}>
              <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="space-y-1.5">
                      <FormLabel className="text-xs font-medium text-muted-foreground">
                        Correo Electrónico
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="ejemplo@correo.com"
                          className="bg-slate-50/50 dark:bg-slate-950/50 border-slate-200 dark:border-slate-800"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <Button
                  type="button"
                  onClick={() => handleShare("email")}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold h-9"
                  disabled={shareImaging.isPending || isSharing}
                >
                  {shareImaging.isPending && form.getValues("type") === "email"
                    ? "Enviando..."
                    : "Compartir por Correo"}
                </Button>
              </form>
            </Form>
          </div>

          {/* WhatsApp Section */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border p-5 shadow-sm space-y-4">
            <div className="flex items-center gap-3 text-green-600 dark:text-green-400">
              <div className="h-8 w-8 rounded-lg bg-green-50 dark:bg-green-900/20 flex items-center justify-center">
                <MessageCircle className="h-4 w-4" />
              </div>
              <h3 className="font-semibold text-sm">Compartir por WhatsApp</h3>
            </div>

            <Form {...form}>
              <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem className="space-y-1.5">
                      <FormLabel className="text-xs font-medium text-muted-foreground">
                        Número de WhatsApp
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={formatPhoneNumber(field.value || "")}
                          placeholder="+1234567890"
                          className="bg-slate-50/50 dark:bg-slate-950/50 border-slate-200 dark:border-slate-800"
                          onChange={(e) => {
                            field.onChange(unformatPhoneNumber(e.target.value));
                          }}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <Button
                  type="button"
                  onClick={() => handleShare("ws")}
                  className="w-full bg-green-600 hover:bg-green-700 text-white text-xs font-semibold h-9"
                  disabled={shareImaging.isPending || isSharing}
                >
                  {shareImaging.isPending && form.getValues("type") === "ws"
                    ? "Enviando..."
                    : "Compartir por WhatsApp"}
                </Button>
              </form>
            </Form>
          </div>

          {/* History Accordion */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border shadow-sm overflow-hidden transition-all">
            <button
              type="button"
              className="w-full flex items-center justify-between p-4 px-5 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors focus:outline-none"
              onClick={() => setIsHistoryOpen(!isHistoryOpen)}
            >
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500">
                  <Calendar className="h-4 w-4" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-xs text-foreground">
                    Historial de Compartidos
                  </h3>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    Ver actividad reciente
                  </p>
                </div>
              </div>
              <ChevronDown
                className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${isHistoryOpen ? "rotate-180" : ""}`}
              />
            </button>

            {isHistoryOpen && (
              <div className="border-t bg-slate-50/50 dark:bg-slate-900/50 max-h-60 overflow-y-auto custom-scrollbar">
                {isLoadingTracking ? (
                  <div className="flex justify-center items-center py-6">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  </div>
                ) : trackingData?.trackingNotifications &&
                  trackingData.trackingNotifications.length > 0 ? (
                  <div className="flex flex-col divide-y divide-slate-100 dark:divide-slate-800/60">
                    {trackingData.trackingNotifications.map((tracking) => (
                      <div
                        key={tracking.id_tracking + tracking.created_at.toString()}
                        className="p-3 px-5 flex items-center justify-between hover:bg-slate-100/50 dark:hover:bg-slate-800/30 transition-colors"
                      >
                        <div className="flex items-center gap-3 overflow-hidden">
                          <div
                            className={`shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${tracking.object_shared === "imaging_access" ? "bg-blue-100 text-blue-600 dark:bg-blue-900/30" : "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30"}`}
                          >
                            {tracking.object_shared === "imaging_access" ? (
                              <Eye className="h-4 w-4" />
                            ) : (
                              <FileText className="h-4 w-4" />
                            )}
                          </div>

                          <div className="min-w-0 flex flex-col justify-center">
                            <div className="flex items-center gap-1.5 text-sm font-medium text-slate-700 dark:text-slate-200 truncate">
                              {tracking.type === "ws" ? (
                                <MessageCircle className="h-3 w-3 text-green-500 shrink-0" />
                              ) : (
                                <Mail className="h-3 w-3 text-blue-500 shrink-0" />
                              )}
                              <span className="truncate">
                                {tracking.type === "ws"
                                  ? formatPhoneNumber(tracking.to)
                                  : tracking.to}
                              </span>
                            </div>
                            <div className="flex items-center text-[10px] text-slate-500 gap-1 mt-0.5">
                              <Clock className="h-2.5 w-2.5" />
                              {formatDate(tracking.created_at)}
                            </div>
                          </div>
                        </div>

                        <Badge
                          variant="secondary"
                          className={`shrink-0 ml-3 font-normal text-[10px] px-2 py-0.5 h-auto ${getStatusColor(tracking.status)}`}
                        >
                          {getStatusText(tracking.status)}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 px-5">
                    <p className="text-[11px] text-muted-foreground text-center py-2">
                      No hay actividad reciente para este estudio
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="p-4 sm:p-6 pt-0 flex justify-center bg-white dark:bg-slate-900 border-t mt-auto">
          <Button
            variant="ghost"
            onClick={onClose}
            className="w-full text-muted-foreground hover:text-foreground text-xs font-medium"
          >
            Cerrar Ventana
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
